import { Text } from '@/components/text';
import { IconSymbol } from '@/components/ui/icon-symbol';
import { RESUME_CONFIGS } from '@/constants/scenarios';
import { ScenarioType } from '@/types/scenario';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { Card } from './ui/card';
import { DonutChart } from './ui/donut-chart';
import { ProgressBar } from './ui/progress-bar';
import { View } from './ui/tw-wrappers';


import { Colors } from '@/constants/theme';
import { LevelGauge } from './ui/level-gauge';
import { LevelLabels } from './ui/level-labels';
import { ProgressBarPrimitive } from './ui/progress-bar-primitive';
import { SemicircleGauge } from './ui/semicircle-gauge';
import { Tooltip } from './ui/tooltip';


export function ScenarioResume({ type }: { type: Exclude<ScenarioType, 'alsoThere'> }) {
  const { t } = useTranslation();
  const config = RESUME_CONFIGS[type];

  return (
    <View className='gap-4'>
      <Card
        backgroundColor='white'
        className="group-hover:border-primary border-2 border-transparent transition-all duration-300"
      >
        {/* 1. SETUP & OPERATIONAL COSTS */}
        {/* header */}
        <View style={styles.sectionHeader} className='mt-0'>
          <Text type='defaultBold' style={styles.sectionTitle}>{t('scenarios.card.setupAndOperationalCosts.title')}</Text>
          <Tooltip
            content={t('scenarios.card.setupAndOperationalCosts.tooltip')}
          >
            <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
          </Tooltip>

        </View>
        <View style={styles.divider} />
        {/* body */}
        <Card backgroundColor='#EFF6FF'>
          <View className='absolute top-2 right-2'>
            <Tooltip
              fullWidth
              align='right'
              content={t(`scenarios.card.setupAndOperationalCosts.value.${type}`)}
            >
              <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
            </Tooltip>
          </View>
          <View className="flex-row items-center justify-between" style={styles.chartContainer}>
            {/* Left: Donut Chart */}
            <View className="flex-1 items-center justify-center">
              <DonutChart
                percentage={config.setupAndOperationalCosts.resume.value}
                size={140}
                color={config.setupAndOperationalCosts.resume.color}
                strokeWidth={16}
              >
                <Text type="defaultBold" style={[styles.sectionTitle, styles.donutChartLabel]} className="text-center text-10 leading-tight px-2">
                  {t('scenarios.card.setupAndOperationalCosts.title')}
                </Text>
              </DonutChart>
            </View>

            {/* Middle: Vertical Divider */}
            <View style={styles.verticalDivider} />

            {/* Right: Progress Bars */}
            <View className="flex-1 h-full justify-center gap-2">
              {config.setupAndOperationalCosts.items?.map((item, index) => (
                <ProgressBar
                  key={item.label}
                  label={t(`scenarios.card.setupAndOperationalCosts.items.${item.label}`)}
                  value={item.value}
                  color={item.color}
                />
              ))}
            </View>
          </View>
        </Card>
        {/* 2.SCABILITY */}
        {/* header */}
        <View style={styles.sectionHeader}>
          <Text type='defaultBold' style={styles.sectionTitle}>{t('scenarios.card.scability.title')}</Text>
          <Tooltip
            content={t('scenarios.card.scability.tooltip')}
          >
            <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
          </Tooltip>
        </View>
        <View style={styles.divider} />
        {/* body */}
        <Card backgroundColor='#EFF6FF'>
          <View className='absolute top-2 right-2'>
            <Tooltip
              fullWidth
              align='right'
              content={t(`scenarios.card.scability.value.${type}`)}
            >
              <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
            </Tooltip>
          </View>
          <View style={styles.sectionGap18}>
            {/* Top thick bar */}
            <View style={styles.sectionGap10}>
              <ProgressBarPrimitive
                value={config.scability.resume.value}
                color={config.scability.resume.color}
                height={16}
              />
              <LevelLabels fontSize={10} color='#6a7282' />
            </View>

            {/* Item list with gauges */}
            <View style={styles.horizontalDividerLight} />
            <View style={styles.sectionGap10Top4}>
              {config.scability.items?.map((item) => (
                <View key={item.label} style={styles.resumeItem}>
                  <ProgressBar
                    key={item.label}
                    label={t(`scenarios.card.scability.items.${item.label}`)}
                    value={item.value}
                    color={item.color}
                  />
                </View>
              ))}
            </View>
          </View>
        </Card>
        {/* 3.TIME TO MARKET */}
        <View style={styles.sectionHeader}>
          <Text type='defaultBold' style={styles.sectionTitle}>{t('scenarios.card.timeToMarket.title')}</Text>
          <Tooltip
            content={t('scenarios.card.timeToMarket.tooltip')}
          >
            <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
          </Tooltip>
        </View>
        <View style={styles.divider} />
        {/* body */}
        <Card backgroundColor='#EFF6FF'>
          <View className='absolute top-2 right-2'>
            <Tooltip
              fullWidth
              align='right'
              content={t(`scenarios.card.timeToMarket.value.${type}`)}
            >
              <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
            </Tooltip>
          </View>
          <View className="flex-1 items-center justify-center">
            <LevelGauge value={config.timeToMarket.resume.value} />
          </View>
        </Card>

        {/* 4.TRANSACTIONAL & PAYMENT CAPABILITIES */}
        {/* header */}
        <View style={styles.sectionHeader}>
          <Text type='defaultBold' style={styles.sectionTitle}>{t('scenarios.card.transactionalAndPaymentCapabilities.title')}</Text>
          <Tooltip
            content={t('scenarios.card.transactionalAndPaymentCapabilities.tooltip')}
          >
            <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
          </Tooltip>
        </View>
        <View style={styles.divider} />
        {/* body */}
        <Card backgroundColor='#EFF6FF'>
          {type === 'internationalInvoicing' && <View className='absolute top-2 right-2'>
            <Tooltip
              fullWidth
              align='right'
              content={t(`scenarios.card.transactionalAndPaymentCapabilities.value.${type}`)}
            >
              <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
            </Tooltip>
          </View>}
          <View style={styles.sectionGap16}>
            {/* Top thick bar */}
            <ProgressBarPrimitive
              value={config.transactionalAndPaymentCapabilities.resume.value}
              color={config.transactionalAndPaymentCapabilities.resume.color}
              height={16}
            />

            {/* Item list with gauges */}
            <View>
              {config.transactionalAndPaymentCapabilities.items?.map((item) => (
                <View key={item.label} style={styles.resumeItem}>
                  <SemicircleGauge
                    value={item.value}
                    color={item.color}
                    size={20}
                    strokeWidth={5}
                  />
                  <Text type='default' style={styles.gaugeItemLabel}>
                    {t(`scenarios.card.transactionalAndPaymentCapabilities.items.${item.label}`)}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </Card>
        {/* 5. CUSTOMER RELATIONSHIP MANAGEMENT */}
        {/* header */}
        <View style={styles.sectionHeader}>
          <Text type='defaultBold' style={styles.sectionTitle}>{t('scenarios.card.customerRelationship.title')}</Text>
          <Tooltip
            content={t('scenarios.card.customerRelationship.tooltip')}
          >
            <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
          </Tooltip>
        </View>
        <View style={styles.divider} />
        {/* body */}
        <Card backgroundColor='#EFF6FF'>
          {(type === 'internationalInvoicing' || type === 'localPartner') && <View className='absolute top-2 right-2'>
            <Tooltip
              fullWidth
              align='right'
              content={t(`scenarios.card.customerRelationship.value.${type}`)}
            >
              <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
            </Tooltip>
          </View>}
          <View style={styles.sectionGap16}>
            {/* Top thick bar */}
            <ProgressBarPrimitive
              value={config.customerRelationship.resume.value}
              color={config.customerRelationship.resume.color}
              height={16}
            />

            {/* Item list with gauges */}
            <View>
              {config.customerRelationship.items?.map((item) => (
                <View key={item.label} style={styles.resumeItem}>
                  <SemicircleGauge
                    value={item.value}
                    color={item.color}
                    size={20}
                    strokeWidth={5}
                  />
                  <Text type='default' style={styles.gaugeItemLabel}>
                    {t(`scenarios.card.customerRelationship.items.${item.label}`)}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </Card>
        {/* 6. BUSINESS AUTONOMY & REVENUE */}
        {/* header */}
        <View style={styles.sectionHeader}>
          <Text type='defaultBold' style={styles.sectionTitle}>{t('scenarios.card.businessAutonomy.title')}</Text>
          <Tooltip
            content={t('scenarios.card.businessAutonomy.tooltip')}
          >
            <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
          </Tooltip>
        </View>
        <View style={styles.divider} />
        {/* body */}
        <Card backgroundColor='#EFF6FF'>
          {(type === 'localPartner') && <View className='absolute top-2 right-2'>
            <Tooltip
              fullWidth
              align='right'
              content={t(`scenarios.card.businessAutonomy.value.${type}`)}
            >
              <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
            </Tooltip>
          </View>}
          <View style={styles.sectionGap16}>
            {/* Top thick bar */}
            <ProgressBarPrimitive
              value={config.businessAutonomy.resume.value}
              color={config.businessAutonomy.resume.color}
              height={16}
            />

            {/* Item list with gauges */}
            <View >
              {config.businessAutonomy.items?.map((item) => (
                <View key={item.label} style={styles.resumeItem}>
                  <SemicircleGauge
                    value={item.value}
                    color={item.color}
                    size={20}
                    strokeWidth={5}
                  />
                  <Text type='default' style={styles.gaugeItemLabel}>
                    {t(`scenarios.card.businessAutonomy.items.${item.label}`)}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </Card>
        {/* 7. COMPLIANCE WITH LOCAL FISCAL REGULATIONS */}
        {/* header */}
        <View style={styles.sectionHeader}>
          <Text type='defaultBold' style={styles.sectionTitle}>{t('scenarios.card.compliance.title')}</Text>
          <Tooltip
            content={t('scenarios.card.compliance.tooltip')}
          >
            <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
          </Tooltip>
        </View>
        <View style={styles.divider} />
        {/* body */}
        <Card backgroundColor='#EFF6FF'>
          {type === 'internationalInvoicing' && <View className='absolute top-2 right-2'>
            <Tooltip
              fullWidth
              align='right'
              content={t(`scenarios.card.compliance.value.${type}`)}
            >
              <IconSymbol name={'info.circle'} size={16} color={Colors.light.primary} />
            </Tooltip>
          </View>}
          <View style={styles.sectionGap16}>
            <ProgressBarPrimitive
              value={config.compliance.resume.value}
              color={config.compliance.resume.color}
              height={16}
            />
          </View>
          <LevelLabels fontSize={10} color='#6a7282' />
        </Card>
      </Card >
    </View >
  );
}


const styles = StyleSheet.create({
  resumeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  resumeItemText: {
    fontSize: 15,
    color: '#374151',
    fontWeight: '500',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
    marginTop: 16,
  },
  sectionTitle: {
    fontSize: 11,
    color: '#305785',
    textTransform: 'uppercase',
  },
  divider: {
    height: 1,
    backgroundColor: '#305785',
    marginBottom: 8,
  },
  chartContainer: {
    gap: 10,
  },
  donutChartLabel: {
    color: '#6a7282',
  },
  verticalDivider: {
    width: 1,
    height: '80%',
    backgroundColor: '#305785',
    opacity: 0.2,
  },
  sectionGap18: {
    gap: 18,
    paddingTop: 10
  },
  sectionGap10: {
    gap: 10,
  },
  horizontalDividerLight: {
    backgroundColor: '#305785',
    opacity: 0.2,
    width: '100%',
    height: 1,
  },
  sectionGap10Top4: {
    gap: 10,
    marginTop: 4,
  },
  sectionGap16: {
    gap: 16,
    paddingTop: 10
  },
  gaugeItemLabel: {
    flex: 1,
    fontSize: 10,
    color: '#6a7282',
  },
});
